import { useState, useEffect } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { toast } from "sonner";
import { useTasks } from "@/hooks/useTasks";
import { useLeads } from "@/hooks/useLeads";
import { getAllActiveUsers } from "@/services/userService";
import { TEST_ADMIN_USER_ID } from "@/lib/constants";
import { Share2, ChevronDown, Users, Mic, RotateCcw } from "lucide-react";
import { format } from "date-fns";
import { useModal } from "@/contexts/ModalContext";
import { useSpeechRecognition } from "@/hooks/useSpeechRecognition";
import { useAuth } from "@/contexts/AuthContext";
import { useAppConfig } from "@/hooks/useAppConfig";
import { useFileUpload } from "@/hooks/useFileUpload";
import { FileUploadSection } from "@/components/common/FileUploadSection";
import { uploadTaskAttachment } from "@/services/taskService";

interface AddReminderDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  leadId: string;
  customerName?: string;
  onSave: (content: string, dueDate: Date) => void;
  hideCustomerSelection?: boolean;
  connectorId?: string; // Channel partner ID
  smContactId?: string; // Banker contact ID
}

export function AddReminderDialog({ 
  open, 
  onOpenChange, 
  leadId, 
  customerName, 
  onSave, 
  hideCustomerSelection = false,
  connectorId,
  smContactId 
}: AddReminderDialogProps) {
  const { addTask, updateTask } = useTasks();
  const { getLead } = useLeads();
  const { user } = useAuth();
  const { config } = useAppConfig();
  const [description, setDescription] = useState("");
  const [dueDate, setDueDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [dueTime, setDueTime] = useState(format(new Date(), "HH:mm"));
  const [shareOpen, setShareOpen] = useState(false);
  const [sharedWith, setSharedWith] = useState<string[]>([]);
  const [shareWithAll, setShareWithAll] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [availableUsers, setAvailableUsers] = useState<Array<{ id?: string; userId?: string; name: string }>>([]);
  const { isListening, toggleVoiceInput } = useSpeechRecognition({
    onTranscript: (text) => setDescription(prev => {
      const newText = prev ? `${prev} ${text}` : text;
      return newText.substring(0, 500);
    })
  });

  // Load real team members from Firebase
  useEffect(() => {
    const loadUsers = async () => {
      try {
        const usersData = await getAllActiveUsers();
        setAvailableUsers(usersData.filter(u => (!u.roles || !u.roles.includes('member')) && (u.userId || u.id) !== TEST_ADMIN_USER_ID));
      } catch (error) {
        console.error('Error loading users:', error);
      }
    };
    loadUsers();
  }, []);

  const fileUpload = useFileUpload({
    maxSizeMB: config.maxFileUploadSizeMB,
    maxFiles: config.maxFileUploads,
    allowedFileTypes: config.allowedFileTypes,
    uploadMode: 'deferred',
  });

  // Auto-populate sharedWith when leadId changes and lead is shared
  // For channel partners and bankers: no auto-sharing, only manual selection
  useEffect(() => {
    if (!leadId || leadId === 'none') {
      setSharedWith([]);
      setShareWithAll(false);
      return;
    }

    // Only auto-share for leads, not for channel partners or bankers
    if (connectorId || smContactId) {
      setSharedWith([]);
      setShareWithAll(false);
      return;
    }

    const lead = getLead(leadId);
    if (lead?.sharedWith && lead.sharedWith.length > 0) {
      // Lead is shared - auto-populate with lead's sharedWith users
      setSharedWith([...lead.sharedWith]);
      setShareWithAll(false);
    } else {
      // Lead not shared or not found
      setSharedWith([]);
      setShareWithAll(false);
    }
  }, [leadId, connectorId, smContactId, getLead]);



  const handleSubmit = async () => {
    if (!description.trim()) {
      toast.error("Please enter what to remind");
      return;
    }

    const dueDateTime = new Date(`${dueDate}T${dueTime}`);
    setIsSubmitting(true);

    try {
      const userId = user?.id || 'unknown';

      const newTask = {
        title: description.split('\n')[0].substring(0, 50),
        description,
        type: 'reminder' as const,
        leadId: leadId,
        connectorId: connectorId,
        smContactId: smContactId,
        dueDate: dueDateTime,
        priority: 'medium' as const,
        completed: false,
        attachments: [] as Awaited<ReturnType<typeof uploadTaskAttachment>>[],
        sharedWith: sharedWith.length > 0 ? sharedWith : undefined,
        createdBy: user?.id || 'unknown', // useTasks hook will set this to user.id (dsaUserId)
        createdByName: user?.name || 'Unknown',
        createdAt: new Date(),
      };

      // Create task first to get the taskId, then upload files into the correct storage path
      const taskId = await addTask(newTask);

      // Upload files now that we have the taskId
      if (fileUpload.pendingFiles.length > 0) {
        toast.info(`Uploading ${fileUpload.pendingFiles.length} file${fileUpload.pendingFiles.length > 1 ? 's' : ''}...`);
        try {
          const uploadedAttachments = await fileUpload.executeUploads(
            (file) => uploadTaskAttachment(file, userId, taskId)
          );
          await updateTask(taskId, { attachments: uploadedAttachments });
        } catch (uploadError) {
          console.error('Error uploading attachments:', uploadError);
          toast.warning('Reminder saved, but some files failed to upload');
        }
      }

      onSave(description, dueDateTime);

      const shareMessage = sharedWith.length > 0
        ? ` (shared with ${shareWithAll ? 'all' : sharedWith.length + ' colleague' + (sharedWith.length > 1 ? 's' : '')})`
        : '';
      const customerMsg = customerName ? ` for ${customerName}` : '';
      toast.success(`Reminder added${customerMsg}${shareMessage}`);

      // Reset form
      setDescription("");
      setDueDate(format(new Date(), "yyyy-MM-dd"));
      setDueTime(format(new Date(), "HH:mm"));
      fileUpload.resetPending();
      setSharedWith([]);
      setShareWithAll(false);
      setShareOpen(false);
      onOpenChange(false);
    } catch (err) {
      console.error('Error saving reminder:', err);
      toast.error('Failed to save reminder');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleToggleShareMember = (memberId: string) => {
    setSharedWith(prev => 
      prev.includes(memberId) 
        ? prev.filter(id => id !== memberId)
        : [...prev, memberId]
    );
    setShareWithAll(false);
  };

  const handleShareWithAll = () => {
    if (shareWithAll) {
      setSharedWith([]);
      setShareWithAll(false);
    } else {
      setSharedWith(availableUsers.map(m => m.userId || m.id!));
      setShareWithAll(true);
    }
  };

  // Register/unregister with ModalContext for centralized back button handling
  const { registerModal, unregisterModal } = useModal();

  useEffect(() => {
    if (!open) return;

    // Determine priority based on what's currently open
    const imageViewerOpen = fileUpload.imageViewerState.isOpen;
    const priority = imageViewerOpen ? 2 : 1;
    const handleClose = () => {
      if (imageViewerOpen) {
        fileUpload.closeImageViewer();
      } else {
        onOpenChange(false);
      }
    };

    registerModal('add-reminder-dialog', handleClose, priority);

    return () => {
      unregisterModal('add-reminder-dialog');
    };
  }, [open, fileUpload.imageViewerState.isOpen, onOpenChange, registerModal, unregisterModal]);

  // Don't render if not open
  if (!open) return null;

  return (
    <>
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="h-[90vh] rounded-t-3xl flex flex-col">
        <SheetHeader className="pb-4 flex-shrink-0">
          <div className="flex items-center justify-center gap-2">
            <SheetTitle className="text-xl font-bold">
              Add Reminder
              {customerName && <span className="text-base font-normal text-muted-foreground block mt-1">For: {customerName}</span>}
            </SheetTitle>
            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={() => { setDescription(''); setDueDate(format(new Date(), "yyyy-MM-dd")); setDueTime(format(new Date(), "HH:mm")); setSharedWith([]); setShareWithAll(false); setShareOpen(false); }}
              className="h-8 w-8 rounded-lg shrink-0"
              title="Clear form"
            >
              <RotateCcw className="h-4 w-4" />
            </Button>
          </div>
        </SheetHeader>
        
        <div className="flex-1 overflow-y-auto px-6">
        <div className="space-y-5 pb-8">
          {/* Description field */}
          <div className="space-y-2">
            <Label>Description *</Label>
            <div className="relative">
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Enter reminder details or tap mic to speak..."
                rows={4}
                maxLength={500}
                className="resize-none pr-12"
              />
              <Button
                type="button"
                variant={isListening ? "default" : "ghost"}
                size="icon"
                className={`absolute right-2 top-2 h-8 w-8 rounded-full ${isListening ? 'bg-red-500 hover:bg-red-600 animate-pulse' : ''}`}
                onClick={toggleVoiceInput}
                title={isListening ? "Stop listening" : "Start voice input"}
              >
                <Mic className={`h-4 w-4 ${isListening ? 'text-white' : ''}`} />
              </Button>
            </div>
            <div className="flex justify-between items-center text-xs">
              <span className="text-muted-foreground">
                {isListening ? '🔴 Listening... tap mic to stop' : 'Tap mic for voice input • Max 500 characters'}
              </span>
              <span className={description.length > 450 ? "text-warning font-medium" : "text-muted-foreground"}>
                {description.length}/500
              </span>
            </div>
          </div>

          {/* Date & Time */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Due Date *</Label>
              <Input
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                className="block [&::-webkit-calendar-picker-indicator]:ml-auto [&::-webkit-calendar-picker-indicator]:opacity-50 hover:[&::-webkit-calendar-picker-indicator]:opacity-100"
              />
            </div>
            <div className="space-y-2">
              <Label>Due Time *</Label>
              <Input
                type="time"
                value={dueTime}
                onChange={(e) => setDueTime(e.target.value)}
                className="block [&::-webkit-calendar-picker-indicator]:ml-auto [&::-webkit-calendar-picker-indicator]:opacity-50 hover:[&::-webkit-calendar-picker-indicator]:opacity-100"
              />
            </div>
          </div>

          {/* Attach Documents/Photos */}
          <FileUploadSection
            files={fileUpload.pendingFiles}
            onCameraCapture={fileUpload.handleCameraCapture}
            onFileUpload={fileUpload.handleFileUpload}
            onRemoveFile={fileUpload.handleRemovePendingFile}
            onImageClick={fileUpload.openImageViewer}
            imageViewerState={{
              ...fileUpload.imageViewerState,
              onClose: fileUpload.closeImageViewer,
            }}
            label="Attach Documents/Photos"
            helpText="Photos, documents, PDFs, etc."
            disabled={isSubmitting}
          />

          {/* Share with colleagues */}
          <Collapsible open={shareOpen} onOpenChange={setShareOpen}>
            <CollapsibleTrigger asChild>
              <Button 
                type="button"
                variant="outline" 
                className="w-full justify-between" 
              >
                <span className="flex items-center gap-2">
                  <Share2 className="h-4 w-4" />
                  <span>
                    {sharedWith.length > 0 
                      ? `Shared with ${shareWithAll ? 'all' : sharedWith.length} colleague${sharedWith.length > 1 ? 's' : ''}`
                      : 'Share with colleagues'
                    }
                  </span>
                </span>
                <ChevronDown className={`h-4 w-4 transition-transform ${shareOpen ? 'rotate-180' : ''}`} />
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="pt-3 space-y-3">
              <div 
                className="flex items-center gap-3 p-3 rounded-lg border cursor-pointer hover:bg-secondary/50 transition-colors"
                onClick={handleShareWithAll}
              >
                <Checkbox checked={shareWithAll} onCheckedChange={handleShareWithAll} />
                <div className="flex-1">
                  <span className="text-sm font-medium">Share with all team members</span>
                </div>
                <Users className="h-4 w-4 text-muted-foreground" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                {availableUsers.map((member) => {
                  const memberId = member.userId || member.id!;
                  return (
                    <div 
                      key={memberId}
                      className="flex items-center gap-2 p-3 rounded-lg border cursor-pointer hover:bg-secondary/50 transition-colors"
                      onClick={() => handleToggleShareMember(memberId)}
                    >
                      <Checkbox 
                        checked={sharedWith.includes(memberId)} 
                        onCheckedChange={() => handleToggleShareMember(memberId)}
                      />
                      <span className="text-sm truncate">{member.name.split(' ')[0]}</span>
                    </div>
                  );
                })}
              </div>
            </CollapsibleContent>
          </Collapsible>
        </div>
        </div>

          {/* Submit Button */}
          <div className="flex-shrink-0 bg-background pt-4 pb-2 border-t">
            <Button
              onClick={handleSubmit}
              disabled={isSubmitting || fileUpload.isUploading}
              className="w-full h-12 text-base font-semibold bg-gradient-to-r from-[#2d5a6b] to-[#4ea39f] hover:from-[#234851] hover:to-[#3d8985]"
            >
              {isSubmitting ? 'Saving...' : 'Create Reminder'}
            </Button>
          </div>
      </SheetContent>
    </Sheet>
    
  </>
  );
}
